<template>
  <div>
    <textarea :id="name" :name="name"></textarea>
  </div>
</template>

<script>
import $ from "jquery";
import "summernote/dist/summernote-bs4";
import "summernote/lang/summernote-tr-TR";
import "summernote/lang/summernote-az-AZ";
import "summernote/lang/summernote-de-DE";
import merge from "lodash/merge";
import axios from "axios";

const SUMMERNOTE_LANG_MAP = {
  tr: "tr-TR",
  az: "az-AZ",
  de: "de-DE",
};

export default {
  name: "SummerNote",

  props: {
    model: {
      required: true,
    },

    height: {
      required: false,
      default: "100px",
    },

    name: {
      type: String,
      required: true,
    },

    forceUpdate: {
      type: Boolean,
      required: false,
      default: false,
    },

    config: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      initializationTimer: null,

      defaultConfig: {
        toolbar: [
          ["style", ["style"]],
          ["font", ["bold", "italic", "underline", "clear"]],
          ["fontname", ["fontname"]],
          ["fontsize", ["fontsize"]],
          ["para", ["ul", "ol", "paragraph"]],
          ["height", ["height"]],
          ["table", ["table"]],
          ["color", ["color"]],
          ["insert", ["link", "picture", "hr"]],
          ["view", ["codeview"]],
        ],
      },
    };
  },

  watch: {
    model(newVal, oldVal) {
      if (newVal && newVal !== oldVal && this.forceUpdate) {
        $("#" + this.name).summernote("code", newVal);
      }
    },
  },

  methods: {
    getEditor() {
      return $("#" + this.name);
    },

    undo() {
      this.getEditor().summernote("undo");
    },

    redo() {
      this.getEditor().summernote("redo");
    },

    toggleCodeview() {
      this.getEditor().summernote("codeview.toggle");
    },

    handleImageUpload(files) {
      for (let i = 0; i < files.length; i++) {
        const formData = new FormData();

        formData.append("file", files[i]);

        axios
            .post("/api/editor/upload", formData, {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            })
            .then((response) => {
              if (
                  response.data &&
                  (response.data.url || response.data.file_link)
              ) {
                const imageUrl =
                    response.data.file_link || response.data.url;

                const image = $("<img>")
                    .attr("src", imageUrl)
                    .css("max-width", "100%");

                $("#" + this.name).summernote(
                    "insertNode",
                    image[0]
                );
              }
            })
            .catch((error) => {
              console.error("Error uploading image:", error);
            });
      }
    },
  },

  mounted() {
    const vm = this;

    let config = merge({}, this.defaultConfig, this.config);

    if (this.config && Array.isArray(this.config.toolbar)) {
      config.toolbar = this.config.toolbar;
    }

    config.height = this.height;

    const locale = this.$i18n && this.$i18n.locale;
    if (locale && SUMMERNOTE_LANG_MAP[locale]) {
      config.lang = SUMMERNOTE_LANG_MAP[locale];
    }

    config.buttons = {
      imageLink: function (context) {
        const ui = $.summernote.ui;

        return ui.button({
          contents: '<i class="note-icon-link"></i>',
          tooltip: vm.$t("general.add_link_to_image"),

          click: function () {
            const target = context.invoke("editor.restoreTarget");

            if (!target) {
              return;
            }

            const $image = $(target);

            const currentUrl = $image.parent("a").attr("href") || "";

            const url = window.prompt(
                vm.$t("general.enter_link_address"),
                currentUrl
            );

            if (url === null) {
              return;
            }

            const trimmedUrl = url.trim();

            if (!trimmedUrl) {
              return;
            }

            if ($image.parent("a").is("a")) {
              $image.parent("a").attr("href", trimmedUrl);
            } else {
              $image.wrap(
                  $("<a>", {
                    href: trimmedUrl,
                    target: "_blank",
                    rel: "noopener noreferrer",
                  })
              );
            }

            context.invoke("editor.focus");

            vm.$emit(
                "change",
                $("#" + vm.name).summernote("code")
            );
          },
        }).render();
      },

      removeImageLink: function (context) {
        const ui = $.summernote.ui;

        return ui.button({
          contents: '<i class="fa fa-unlink"></i>',
          tooltip: vm.$t("general.remove_image_link"),

          click: function () {
            const target = context.invoke("editor.restoreTarget");

            if (!target) {
              return;
            }

            const $image = $(target);

            if ($image.parent("a").is("a")) {
              $image.unwrap();
            }

            context.invoke("editor.focus");

            vm.$emit(
                "change",
                $("#" + vm.name).summernote("code")
            );
          },
        }).render();
      },
    };

    config.popover = {
      image: [
        [
          "image",
          [
            "resizeFull",
            "resizeHalf",
            "resizeQuarter",
            "resizeNone",
          ],
        ],
        [
          "float",
          [
            "floatLeft",
            "floatRight",
            "floatNone",
          ],
        ],
        [
          "link",
          [
            "imageLink",
            "removeImageLink",
          ],
        ],
        [
          "remove",
          ["removeMedia"],
        ],
      ],
    };

    config.callbacks = {
      onInit: function () {
        $("#" + vm.name).summernote(
            "code",
            vm.model
        );
      },

      onChange: function () {
        vm.$emit(
            "change",
            $("#" + vm.name).summernote("code")
        );
      },

      onBlur: function () {
        vm.$emit(
            "change",
            $("#" + vm.name).summernote("code")
        );
      },

      onImageUpload: function (files) {
        vm.handleImageUpload(files);
      },
    };

    this.initializationTimer = setTimeout(() => {
      const editor = $("#" + vm.name);

      editor.summernote(config);
    }, 100);
  },

  beforeDestroy() {
    clearTimeout(this.initializationTimer);

    const editor = $("#" + this.name);

    if (
        typeof editor.summernote === "function" &&
        editor.data("summernote")
    ) {
      editor.summernote("destroy");
    }
  },
};
</script>

<style>
@import "~summernote/dist/summernote-bs4.css";
</style>

<style>
.note-editor.note-frame.card button.note-btn.btn.btn-light.btn-sm.dropdown-toggle::after {
  content: unset !important;
}

.note-editor.note-frame.card div.btn-group.note-color-all > button.note-btn.btn.btn-light.btn-sm.dropdown-toggle {
  padding: 0px !important;
  padding-left: 5px;
}
</style>